/** document-svc dossier routes (blueprint §5.5; wired in Phase 5.6). */

import type {
  Dossier,
  DossierCreateRequest,
  DossierCreated,
  DossierStatus,
  DossierSummary,
  ReminderSent,
  SignedLink,
  Uuid,
} from "@re-platform/types";

import type { ApiClient } from "../client";

export const dossierService = {
  create(client: ApiClient, body: DossierCreateRequest) {
    return client.request<DossierCreated>("/document/dossiers", {
      method: "POST",
      body,
    });
  },

  list(
    client: ApiClient,
    params?: {
      client_id?: Uuid;
      status?: DossierStatus;
      limit?: number;
      offset?: number;
    },
  ) {
    return client.request<DossierSummary[]>("/document/dossiers", {
      query: params,
    });
  },

  get(client: ApiClient, dossierId: Uuid) {
    return client.request<Dossier>(`/document/dossiers/${dossierId}`);
  },

  /**
   * Approve or reject a document awaiting human confirmation (V074).
   *
   * Approving satisfies the requirement exactly as VALIDATED does, and records
   * who. Rejecting frees the slot for a re-upload and tells the client why.
   */
  decideSlot(
    client: ApiClient,
    dossierId: Uuid,
    templateItemId: Uuid,
    body: {
      decision: "approve" | "reject";
      reason?: string;
      /** Spec 019 — the document the agent reviewed; a mismatch is a 409. */
      document_id?: Uuid;
    },
  ) {
    return client.request<{
      dossier_id: Uuid;
      template_item_id: Uuid;
      status: string;
      dossier_completed: boolean;
    }>(`/document/dossiers/${dossierId}/slots/${templateItemId}/approval`, {
      method: "POST",
      body,
    });
  },

  mintSignedLink(client: ApiClient, dossierId: Uuid) {
    return client.request<SignedLink>(
      `/document/dossiers/${dossierId}/signed-link`,
      {
        method: "POST",
      },
    );
  },

  remind(client: ApiClient, dossierId: Uuid) {
    return client.request<ReminderSent>(
      `/document/dossiers/${dossierId}/remind`,
      {
        method: "POST",
      },
    );
  },

  /** Set (or clear with null) the per-dossier reminder cadence override. */
  putReminderConfig(
    client: ApiClient,
    dossierId: Uuid,
    reminderConfigOverride: Record<string, unknown> | null,
  ) {
    return client.request<Dossier>(
      `/document/dossiers/${dossierId}/reminder-config`,
      {
        method: "PUT",
        body: { reminder_config_override: reminderConfigOverride },
      },
    );
  },
};
